import React, { useState, useEffect } from "react";
import CaseStudyChart from "@/components/CaseStudyChart";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { VIDEO_REVIEW_URL } from "@/lib/cta";

const ff = "'Barlow', sans-serif";
const cyan = "#57d7e2";
const CDN = "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj";
const LOGO_ASSETS = [
  `/4941-9030.webp`,`/4941-9031.webp`,`/4941-9032.webp`,`/4941-9033.webp`,
  `/4941-9034.webp`,`/4941-9035.webp`,`/4941-9036.webp`,`/4941-9037.webp`,
  `/4941-9038.webp`,`/4941-9039.webp`,`/4941-9040.webp`,`/4941-9041.webp`,
  `/4941-9042.webp`,`/4941-9043.webp`,`/4941-9044.webp`,`/4941-9045.webp`,
  `/4941-9046.webp`,`/4941-9047.webp`,`/4941-9048.webp`,`/4941-9049.webp`,
  `/4941-9050.webp`,`/4941-9051.webp`,`/4941-9052.webp`,`/4941-9053.webp`,
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663444343231/KfbWTnTA9XstaA7FAytXMj/smartfog_87dd78bd.webp",
];
const ctaStyle: React.CSSProperties = { display:"inline-flex",alignItems:"center",gap:"8px",background:"linear-gradient(135deg,#85e2e9 0%,#20b0bc 100%)",border:`1px solid ${cyan}`,borderRadius:"88px",padding:"16px 32px",boxShadow:"0px 2px 15px 0px rgba(87,215,226,0.20)",color:"#121212",fontSize:"16px",fontWeight:600,fontFamily:ff,textDecoration:"none",transition:"opacity 0.2s" };
const microCommit: React.CSSProperties = { display:"block",color:"rgba(255,255,255,0.4)",fontSize:"13px",fontFamily:ff,marginTop:"12px",lineHeight:1.5 };

const PAIN_POINTS = [
  { n:"01", title:"Your site is slow and it's costing you rankings", desc:"Every 100ms of additional load time reduces conversion rates by 1%. Google's Core Web Vitals are a direct ranking factor — a slow site is a revenue leak you can measure." },
  { n:"02", title:"Your CMS is a crawl budget nightmare", desc:"Duplicate content, bloated JavaScript, and unoptimized URL structures mean Google is wasting its crawl budget on pages that don't matter — and missing the ones that do." },
  { n:"03", title:"Your site can't scale with your SEO ambitions", desc:"Programmatic SEO, content hubs, and landing page infrastructure require a build that was designed for scale — not a template that breaks when you add 500 pages." },
  { n:"04", title:"Your dev team and SEO team aren't aligned", desc:"When developers build without SEO context, every release risks a traffic drop. We bridge the gap — developer-ready SEO specs that eliminate implementation errors." },
];

const DELIVERABLES = [
  { title:"SEO-Ready Technical Architecture", desc:"URL structures, internal linking systems, and site hierarchies built to maximize crawl efficiency and organic performance from day one." },
  { title:"Core Web Vitals Engineering", desc:"LCP, CLS, and INP optimized through code-level performance work — image optimization, lazy loading, critical CSS, and server response improvements." },
  { title:"Programmatic Page Infrastructure", desc:"Scalable templating systems that generate thousands of SEO-optimized pages from structured data — without proportional development cost." },
  { title:"Headless & Custom CMS Builds", desc:"Fast, flexible headless builds on Next.js, Astro, or your preferred stack — with CMS integrations that keep content teams moving without developer bottlenecks." },
  { title:"Schema & Structured Data Implementation", desc:"Comprehensive schema markup validated and deployed across all page types — unlocking rich results and helping Google understand your content hierarchy." },
  { title:"Developer-SEO Collaboration Protocol", desc:"Ongoing technical SEO review of every release — so your development velocity never comes at the cost of organic performance." },
];

const CASE_STUDIES = [
  { slug: "solar-installer", industry: "Renewable Energy", metric:"$1.1m", metricLabel:"Revenue generated", desc:"A full technical rebuild — new site architecture, Core Web Vitals remediation, and schema implementation — unlocked $1.5m in SEO-driven pipeline and $1.1m in revenue for a venture-backed solar group.", before:"Site not optimized for organic", after:"$1.1m revenue · $1.5m pipeline", timeframe:"10 months" },
  { slug: "branding-agency", industry: "Branding & Design Services", metric:"3.5×", metricLabel:"Return on investment", desc:"A performance-first rebuild of a global branding studio's site became their primary lead generation channel — generating $420K in closed revenue and a 3.5× return on their total investment.", before:"$120K investment", after:"$420K revenue · 3.5× ROI", timeframe:"12 months" },
  { slug: "storage-company", industry: "Storage & Real Estate", metric:"+513%", metricLabel:"Organic click growth", desc:"A programmatic landing page system and technical SEO overhaul scaled a US storage company from 150 to 920 daily organic clicks in 14 months.", before:"150 daily organic clicks", after:"920 daily clicks (+513%)", timeframe:"14 months" },
];

const FAQS = [
  { q:"Do you build websites or just do SEO?", a:"Both — and the integration is the point. We build sites that are engineered for organic performance from the ground up. Every technical decision — architecture, rendering strategy, URL structure — is made with search visibility and revenue in mind." },
  { q:"What tech stack do you build on?", a:"We work across modern stacks — Next.js, Astro, Nuxt, and headless CMS setups (Contentful, Sanity, Prismic). We'll recommend the right stack for your performance requirements, content team's workflow, and SEO ambitions." },
  { q:"Can you work with our existing development team?", a:"Yes. We can act as your SEO-development bridge — providing developer-ready technical specs, reviewing releases for SEO impact, and implementing fixes directly. Many clients use us to upskill their dev team on SEO best practices." },
  { q:"How does web development fit into the Full-Stack Search Method™?", a:"The build is the foundation. Without a technically sound site, every other SEO investment — content, links, strategy — underperforms. We engineer the site to support the full system, so every layer compounds on top of a solid base." },
  { q:"How do I know what my technical issues are costing me?", a:"That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll identify the key technical issues on your site and estimate the revenue impact of fixing them. No commitment required." },
];

function FaqItem({ faq, index, openFaq, setOpenFaq, isLast }: { faq:{q:string;a:string}; index:number; openFaq:number|null; setOpenFaq:(i:number|null)=>void; isLast:boolean }) {
  const isOpen = openFaq === index;
  return (
    <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)",borderBottom:isLast?"1px solid rgba(255,255,255,0.08)":"none" }}>
      <button type="button" onClick={()=>setOpenFaq(isOpen?null:index)} style={{ width:"100%",padding:"24px 0",display:"flex",alignItems:"center",justifyContent:"space-between",gap:"16px",background:"transparent",border:"none",cursor:"pointer",textAlign:"left" as const }}>
        <span style={{ fontSize:"17px",fontWeight:600,color:"white",fontFamily:ff,lineHeight:1.4 }}>{faq.q}</span>
        <span style={{ color:cyan,fontSize:"24px",fontWeight:300,flexShrink:0,transform:isOpen?"rotate(45deg)":"none",transition:"transform 0.2s",lineHeight:1 }}>+</span>
      </button>
      {isOpen&&<div style={{ paddingBottom:"24px" }}><p style={{ margin:0,fontSize:"15px",color:"rgba(255,255,255,0.6)",fontFamily:ff,lineHeight:1.8 }}>{faq.a}</p></div>}
    </div>
  );
}

export default function WebDevelopment() {
  useSeoMeta({
    title: "SEO Web Development | Fast, Clean, Rank-Ready Sites",
    description: "We build fast, accessible, search-engine-friendly websites that support your organic growth goals — without creating technical debt for your team.",
    canonical: "/web-development",
    ogType: "website",
    jsonLd: [
    {
      "@type": "Service",
      "serviceType": "Web Development",
      "provider": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" },
      "name": "SEO Web Development",
      "description": "Fast, crawlable, revenue-ready websites.",
      "url": "https://www.joinindexed.com/web-development"
    }
  ,
  
  {
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Do you build websites or just do SEO?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Both — and the integration is the point. We build sites that are engineered for organic performance from the ground up. Every technical decision — architecture, rendering strategy, URL structure — is made with search visibility and revenue in mind."
        }
      },
      {
        "@type": "Question",
        "name": "What tech stack do you build on?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We work across modern stacks — Next.js, Astro, Nuxt, and headless CMS setups (Contentful, Sanity, Prismic). We'll recommend the right stack for your performance requirements, content team's workflow, and SEO ambitions."
        }
      },
      {
        "@type": "Question",
        "name": "Can you work with our existing development team?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes. We can act as your SEO-development bridge — providing developer-ready technical specs, reviewing releases for SEO impact, and implementing fixes directly. Many clients use us to upskill their dev team on SEO best practices."
        }
      },
      {
        "@type": "Question",
        "name": "How does web development fit into the Full-Stack Search Method™?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The build is the foundation. Without a technically sound site, every other SEO investment — content, links, strategy — underperforms. We engineer the site to support the full system, so every layer compounds on top of a solid base."
        }
      },
      {
        "@type": "Question",
        "name": "How do I know what my technical issues are costing me?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "That's exactly what our free video review is for. In a 10–15 minute video recorded for you, we'll identify the key technical issues on your site and estimate the revenue impact of fixing them. No commitment required."
        }
      }
    ]
  },
  {
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.joinindexed.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Services",
        "item": "https://www.joinindexed.com/seo-agency"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Web Development",
        "item": "https://www.joinindexed.com/web-development"
      }
    ]
  }],
  });
  const isMobile = useIsMobile();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <SiteLayout>

      {/* ── HERO SECTION ── */}
      <div style={{ position: "relative", overflow: "hidden", paddingTop: isMobile ? "60px" : "0" }}>
        {/* Background image */}
        <div style={{
          position: "absolute", top: "-56px", left: "-51px",
          width: "calc(100% + 102px)", height: "calc(100% + 112px)",
          overflow: "hidden", zIndex: 0,
        }}>
          <div style={{ width: "100%", height: "100%", background: "radial-gradient(115% 125% at 78% -10%, #0f3036 0%, #0b1719 38%, #070b0b 72%)" }} />
        </div>
        {/* Cyan glow left */}
        <div style={{ position: "absolute", width: "286px", height: "536px", left: "-80px", top: "-97px", background: "#3aefff", filter: "blur(175px)", opacity: 0.15, transform: "rotate(15deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Cyan glow center */}
        <div style={{ position: "absolute", width: "474px", height: "474px", left: "473px", top: "-300px", background: "#3aefff", filter: "blur(175px)", opacity: 0.35, transform: "rotate(45deg)", zIndex: 1, pointerEvents: "none" }} />
        {/* Bottom gradient */}
        <div style={{
          position: "absolute", width: "100%", height: "60%", left: 0, bottom: 0,
          background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,0.92) 80%, rgba(0,0,0,1) 100%)",
          zIndex: 2, pointerEvents: "none",
        }} />
        {/* Left text shadow overlay */}
        <div style={{ position: "absolute", top: 0, left: 0, width: "55%", height: "100%", background: "linear-gradient(to right, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 60%, rgba(0,0,0,0) 100%)", zIndex: 3, pointerEvents: "none" }} />
        <div style={{ position: "relative", zIndex: 10, display: "flex", flexDirection: "column", paddingTop: "66px" }}>
          <div style={{ flex: 1, display: "flex", alignItems: "center", padding: isMobile ? "60px 20px 40px" : "60px 72px 40px", maxWidth: "1440px", margin: "0 auto", width: "100%" }}>
            <div style={{ maxWidth: "620px" }}>
              {/* Star rating */}
              <div style={{
                display: "inline-flex", alignItems: "center", gap: "8px",
                marginBottom: "24px", padding: "6px 14px",
                borderRadius: "100px", border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.04)",
              }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="#FFD700">
                  <path d="M8 1l1.8 4.6L15 6.2l-3.6 3.5.85 4.95L8 12.3l-4.25 2.35.85-4.95L1 6.2l5.2-.6L8 1z"/>
                </svg>
                <span style={{ color: "rgba(255,255,255,0.85)", fontSize: "14px", fontWeight: 500, fontFamily: ff }}>5.0 from 100+ reviews</span>
              </div>
              {/* Headline */}
              <h1 style={{
                color: "white", fontSize: isMobile ? "36px" : "clamp(40px, 4vw, 60px)", fontWeight: 700,
                lineHeight: 1.08, margin: "0 0 24px", fontFamily: ff, letterSpacing: "-0.5px",
                maxWidth: "560px",
              }}>
                Web Development that{" "}<span style={{ color: "#57d7e2" }}>performs and converts.</span>
              </h1>
              {/* Description */}
              <p style={{
                color: "rgba(255,255,255,0.75)", fontSize: "18px", fontWeight: 400,
                lineHeight: 1.6, margin: "0 0 36px", fontFamily: ff, maxWidth: "520px",
              }}>
                Your site\'s technical issues are costing you revenue. We can show you exactly how much.
              </p>
              {/* CTA */}
              <a
                href={VIDEO_REVIEW_URL}
                style={{
                  display: "inline-flex", alignItems: "center", alignSelf: "flex-start", padding: "16px 32px",
                  background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                  borderRadius: "88px", border: "1px solid #57d7e2",
                  boxShadow: "0px 2px 15px 0px rgba(87,215,226,0.25)",
                  color: "#121212", fontSize: "18px", fontWeight: 600,
                  fontFamily: ff, textDecoration: "none",
                  transition: "opacity 0.2s, transform 0.2s",
                }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.9"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-2px)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
              >
                Get my free video review →
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Logo marquee */}
      <section style={{ background:"#080a0a" }}>
        <div style={{ position:"relative",borderTop:"1px solid rgba(255,255,255,0.08)",padding:"16px 0",overflow:"hidden" }}>
          <div style={{ position:"absolute",inset:0,background:"linear-gradient(to right,#000202 0%,transparent 12%,transparent 88%,#000202 100%)",zIndex:2,pointerEvents:"none" }} />
          <div style={{ display:"flex",alignItems:"center",gap:"50px",animation:"scroll-logos 50s linear infinite",width:"max-content" }}>
            {[...LOGO_ASSETS,...LOGO_ASSETS].map((src,idx)=>(
              <img loading="lazy" key={idx} src={src} alt={`client logo ${idx}`} style={{ width: "135px", height: "90px", objectFit: "contain", flexShrink: 0, filter: "grayscale(1) brightness(2)" }} />
            ))}
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 20px",textAlign:"center" as const,width:"100%"}}>
            The technical issues<br /><span style={{ color:cyan }}>quietly costing you revenue.</span>
          </h2>
          <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.7,margin:"0 0 48px",textAlign:"center" as const,maxWidth:"640px",marginLeft:"auto",marginRight:"auto" }}>If any of these sound familiar, your site has technical debt that's actively costing you rankings and revenue.</p>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "1fr 1fr",gap:"18px" }}>
            {PAIN_POINTS.map((p,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:isMobile ? "28px" : "36px",display:"flex",flexDirection:"column" as const,gap:"12px" }}>
                <div style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",width:"36px",height:"36px",borderRadius:"10px",background:"rgba(87,215,226,0.08)",border:"1px solid rgba(87,215,226,0.15)" }}>
                  <span style={{ color:cyan,fontSize:"14px",fontWeight:700,fontFamily:ff }}>{p.n}</span>
                </div>
                <h3 style={{ color:"white",fontSize:"18px",fontWeight:700,fontFamily:ff,margin:0,lineHeight:1.3 }}>{p.title}</h3>
                <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.7,margin:0 }}>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What is section */}
      <section className="section-grid" style={{ background:"#080808",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"800px",margin:"0 auto",textAlign:"center" as const }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 24px" ,textAlign:"center" as const,width:"100%"}}>
            Development engineered<br /><span style={{ color:cyan }}>for organic growth.</span>
          </h2>
          <p style={{ color:"rgba(255,255,255,0.6)",fontSize:"18px",fontFamily:ff,lineHeight:1.8,margin:0 }}>
            Most development agencies build to spec. We build to perform. Every architectural decision — rendering strategy, URL structure, internal linking, schema implementation — is made with organic search performance and commercial outcomes in mind. The result is a site that's fast, scalable, and built to compound your SEO investment over time.
          </p>
        </div>
      </section>

      {/* Deliverables */}
      <section style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 20px",textAlign:"center" as const,width:"100%"}}>
            Inside every<br /><span style={{ color:cyan }}>development engagement.</span>
          </h2>
          <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"16px",fontFamily:ff,lineHeight:1.7,margin:"0 0 48px",textAlign:"center" as const,maxWidth:"640px",marginLeft:"auto",marginRight:"auto" }}>
            Not a WordPress theme. Not a Webflow template. A bespoke, performance-first build engineered to support the Full-Stack Search Method™ and compound your organic growth over time.
          </p>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {DELIVERABLES.map((d,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:isMobile ? "28px" : "32px",display:"flex",flexDirection:"column" as const,gap:"14px" }}>
                <div style={{ width:"36px",height:"36px",borderRadius:"50%",background:"rgba(87,215,226,0.08)",border:"1px solid rgba(87,215,226,0.15)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                  <svg width="16" height="16" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke={cyan} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                <div style={{ color:"white",fontSize:"16px",fontWeight:700,fontFamily:ff,lineHeight:1.3 }}>{d.title}</div>
                <div style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.65 }}>{d.desc}</div>
              </div>
            ))}
          </div>
          <div style={{ textAlign:"center",marginTop:"48px" }}>
            <a href="/method" style={ctaStyle}>See how it works</a>
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="section-grid" style={{ background:"#080808",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <div style={{ display:"flex",alignItems:"flex-end",justifyContent:"space-between",gap:"16px",marginBottom:"64px" }}>
            <h2 style={{ color:"white",fontSize:"clamp(32px,5vw,48px)",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:0,textAlign:"left" as const }}>
              Real results,<br /><span style={{ color:cyan }}>real businesses.</span>
            </h2>
            <a href="/client-stories"
              style={{ color: cyan, fontSize: "16px", fontWeight: 600, fontFamily: ff, textDecoration: "none", transition: "opacity 0.2s", flexShrink: 0 }}
              onMouseEnter={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "0.7"}
              onMouseLeave={e => (e.currentTarget as HTMLAnchorElement).style.opacity = "1"}>
              See all →
            </a>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {CASE_STUDIES.map((cs,i)=>(
              <a key={i} href={`/client-stories/${cs.slug}`} style={{ textDecoration: "none", color: "inherit", display: "block" }}>
              <div style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"32px",display:"flex",flexDirection:"column" as const,gap:"20px" }}>
                <span style={{ color:"rgba(255,255,255,0.4)",fontSize:"11px",fontWeight:700,fontFamily:ff,letterSpacing:"0.1em",textTransform:"uppercase" as const }}>{cs.industry}</span>
                <div>
                  <div style={{ color:cyan,fontSize:"44px",fontWeight:700,fontFamily:ff,lineHeight:1 }}>{cs.metric}</div>
                  <div style={{ color:"rgba(255,255,255,0.5)",fontSize:"13px",fontFamily:ff,marginTop:"6px" }}>{cs.metricLabel}</div>
                </div>
                <CaseStudyChart slug={cs.slug} ariaLabel={`${cs.industry}: ${cs.metric} ${cs.metricLabel.toLowerCase()}`} />
                  <p style={{ color:"rgba(255,255,255,0.7)",fontSize:"14px",fontFamily:ff,lineHeight:1.65,margin:0,flex:1 }}>{cs.desc}</p>
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)",paddingTop:"20px",display:"grid",gridTemplateColumns:"1fr 1fr",gap:"10px" }}>
                  <div><div style={{ fontSize:"10px",color:"rgba(255,255,255,0.3)",fontFamily:ff,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase" as const,marginBottom:"4px" }}>Before</div><div style={{ fontSize:"13px",fontWeight:600,color:"rgba(255,255,255,0.6)",fontFamily:ff }}>{cs.before}</div></div>
                  <div><div style={{ fontSize:"10px",color:cyan,fontFamily:ff,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase" as const,marginBottom:"4px" }}>After</div><div style={{ fontSize:"13px",fontWeight:600,color:"white",fontFamily:ff }}>{cs.after}</div></div>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "12px", color: "rgba(255,255,255,0.3)", fontFamily: ff }}>Achieved in {cs.timeframe}</span>
                <span style={{ fontSize: "13px", color: cyan, fontFamily: ff, fontWeight: 600 }}>Read case study →</span>
              </div>
              </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-grid" style={{ background:"#0a0c0c",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto" }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 56px" ,textAlign:"center" as const,width:"100%"}}>
            What clients say<br /><span style={{ color:cyan }}>about working with us.</span>
          </h2>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px" }}>
            {[
              { quote:"Exceptional communication throughout the project. The team showed a deep grasp of our goals and delivered a flawless final product that exceeded our expectations.", name:"Flavio", role:"Growth Marketing Director", company:"B2B SaaS" },
              { quote:"Great team to work with, very happy with the consultancy and final documents. They translated complex strategy into something our whole team could act on.", name:"Kirsten", role:"Senior Marketing Manager", company:"Professional Services" },
              { quote:"Indexed turned SEO from a cost center into our highest-ROI acquisition channel. The pipeline numbers speak for themselves — $1.5m in 10 months.", name:"James", role:"CEO", company:"Renewable Energy" },
            ].map((t,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"36px",display:"flex",flexDirection:"column" as const,gap:"24px" }}>
                <div style={{ display:"flex",gap:"3px" }}>{[...Array(5)].map((_,s)=><svg key={s} width="14" height="14" viewBox="0 0 12 12" fill="#57d7e2"><path d="M6 1l1.5 3 3.5.5-2.5 2.5.5 3.5L6 9l-3 1.5.5-3.5L1 4.5l3.5-.5z"/></svg>)}</div>
                <p style={{ color:"rgba(255,255,255,0.8)",fontSize:"16px",fontFamily:ff,lineHeight:1.75,margin:0,flex:1,fontStyle:"italic" }}>"{t.quote}"</p>
                <div style={{ borderTop:"1px solid rgba(255,255,255,0.07)",paddingTop:"20px",display:"flex",alignItems:"center",gap:"14px" }}>
                  <div style={{ width:"40px",height:"40px",borderRadius:"50%",background:`linear-gradient(135deg,${cyan}30,${cyan}10)`,border:`1px solid ${cyan}40`,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    <span style={{ color:cyan,fontSize:"15px",fontWeight:700,fontFamily:ff }}>{t.name[0]}</span>
                  </div>
                  <div>
                    <div style={{ color:"white",fontSize:"14px",fontWeight:700,fontFamily:ff }}>{t.name}</div>
                    <div style={{ color:"rgba(255,255,255,0.4)",fontSize:"13px",fontFamily:ff }}>{t.role} · {t.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section className="section-grid" style={{ background:"#080808",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"1100px",margin:"0 auto",textAlign:"center" as const }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 16px" ,textAlign:"center" as const,width:"100%"}}>
            What happens<br /><span style={{ color:cyan }}>when you reach out.</span>
          </h2>
          <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"18px",fontFamily:ff,lineHeight:1.7,margin:"0 0 64px",maxWidth:"560px",marginLeft:"auto",marginRight:"auto" }}>No sales calls. No vague proposals. Just a clear process from first contact to measurable results.</p>
          <div style={{ display:"grid",gridTemplateColumns:isMobile ? "1fr" : "repeat(3,1fr)",gap:"18px",textAlign:"left" as const }}>
            {[
              { n:"01", title:"We analyze your technical revenue gap", desc:"We review your current site's technical health — Core Web Vitals, crawl efficiency, architecture — and identify the issues costing you the most in rankings and revenue." },
              { n:"02", title:"We show you what fixing it is worth", desc:"We quantify the revenue impact of the key technical issues and show you exactly what a performance-first rebuild or targeted remediation could return." },
              { n:"03", title:"We build and execute your growth engine", desc:"If it's a fit, we get to work. Web development is one pillar of the Full-Stack Search Method™ — the build becomes the foundation for everything else." },
            ].map((step,i)=>(
              <div key={i} style={{ background:"#111111",border:"1px solid rgba(255,255,255,0.07)",borderRadius:"16px",padding:"36px" }}>
                <div style={{ color:cyan,fontSize:"13px",fontWeight:700,fontFamily:ff,letterSpacing:"0.08em",marginBottom:"16px" }}>{step.n}</div>
                <h3 style={{ color:"white",fontSize:"20px",fontWeight:700,fontFamily:ff,margin:"0 0 12px",lineHeight:1.3 }}>{step.title}</h3>
                <p style={{ color:"rgba(255,255,255,0.5)",fontSize:"14px",fontFamily:ff,lineHeight:1.7,margin:0 }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="section-grid" style={{ background:"#0a0a0a",padding:isMobile ? "60px 20px" : "100px 72px" }}>
        <div style={{ maxWidth:"860px",margin:"0 auto" }}>
          <h2 style={{ color:"white",fontSize:isMobile?"32px":"48px",fontWeight:700,fontFamily:ff,lineHeight:1.1,letterSpacing:"-0.02em",margin:"0 0 64px" ,textAlign:"center" as const,width:"100%"}}>
            Still on the fence?<br /><span style={{ color:cyan }}>Questions answered.</span>
          </h2>
          <div>{FAQS.map((faq,i)=><FaqItem key={i} faq={faq} index={i} openFaq={openFaq} setOpenFaq={setOpenFaq} isLast={i===FAQS.length-1}/>)}</div>
        </div>
      </section>

      {/* ── FREE REVENUE GAP ANALYSIS CTA ── */}
      <section className="section-grid" style={{ background: "#080a0a", padding: isMobile ? "60px 20px" : "100px 72px", position: "relative", overflow: "hidden" }}>
        <div style={{ maxWidth: "760px", margin: "0 auto", display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "32px" }}>
          {/* Heading */}
          <h2 style={{ color: "white", fontSize: isMobile ? "28px" : "48px", fontWeight: 700, fontFamily: ff, margin: 0, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            Find out what your site is losing.<br />{" "}<span style={{ color: "#57d7e2" }}>It's free.</span>
          </h2>
          {/* Body */}
          <p style={{ color: "rgba(255,255,255,0.6)", fontSize: "18px", fontFamily: ff, lineHeight: 1.7, margin: 0, maxWidth: "520px" }}>
            In a free personalized video review, a top-1% SEO records a walkthrough of your site and shows you exactly what's holding your rankings back — and what fixing it is worth. No pitch. No obligation.
          </p>
          {/* CTA */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "14px" }}>
            <a
              href={VIDEO_REVIEW_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: isMobile ? "14px 28px" : "20px 48px",
                background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
                borderRadius: "88px",
                border: "1px solid #57d7e2",
                boxShadow: "0px 4px 32px rgba(87,215,226,0.30)",
                textDecoration: "none",
                color: "#0a0c0c",
                fontSize: isMobile ? "15px" : "17px",
                fontWeight: 700,
                fontFamily: ff,
                letterSpacing: "-0.01em",
                transition: "opacity 0.2s, transform 0.2s",
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
            >
              Get my free video review →
            </a>
            <span style={{ color: "rgba(255,255,255,0.35)", fontSize: "13px", fontFamily: ff }}>
              No call &middot; No pitch &middot; We take on 3–4 new clients per month — request yours to check availability
            </span>
          </div>
          {/* Stat strip */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", marginTop: "16px", borderTop: "1px solid rgba(255,255,255,0.07)", paddingTop: "32px", width: "100%", gap: "0" }}>
            {[
              { metric: "5.0 ★", label: "from 100+ reviews" },
              { metric: "100+", label: "engagements delivered" },
              { metric: "Avg. 3.5×", label: "return on SEO investment" },
            ].map((s, i) => (
              <div key={s.metric} style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px", padding: isMobile ? "12px 8px" : "12px 16px", borderLeft: i > 0 ? "1px solid rgba(255,255,255,0.1)" : "none", textAlign: "center" as const }}>
                <span style={{ color: "#57d7e2", fontSize: isMobile ? "18px" : "22px", fontWeight: 700, fontFamily: ff, lineHeight: 1 }}>{s.metric}</span>
                <span style={{ color: "rgba(255,255,255,0.45)", fontSize: isMobile ? "11px" : "13px", fontFamily: ff, lineHeight: 1.3 }}>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
